import { useCallback, useEffect, useRef } from 'react';

import { Native } from '@givebacks/api';
import { useQueryClient } from '@tanstack/react-query';

import { useCreateShortSession } from '../../api';
import { SUBDOMAINS, specificSubDomain } from '../../utils';

interface IMessageFromNative {
  exitStatus: 200 | 500;
  sender: 'native' | 'native_browser';
  location?: string;
  action?: string;
  appState?: 'active' | 'background' | 'inactive' | 'unknown' | 'extension';
  extensionInstallationStatus?: boolean;
}

// You should be passing in referentially stable functions here and handling the side effects else where.
interface IOpenNativeBrowser {
  onSuccess?: () => void;
  onFailure?: () => void;
  onExit?: () => void;
}

interface IOpenDonations extends IOpenNativeBrowser {
  type: 'cause_donation' | 'direct_donation' | 'manage_donations';
}
enum NativeDonations {
  manage_donations = '/donate/manage',
  cause_donation = '/donate',
  direct_donation = '/direct_donation/',
}

type NativeIntercomArgs = {
  onSuccess: () => void;
  onFailure: () => void;
  onExit: () => void;
};

const nativeIntercom = ({ onSuccess, onFailure, onExit }: NativeIntercomArgs) => {
  const intercom = (event: { data: string }) => {
    try {
      const json = JSON.parse(event.data);
      const { exitStatus } = json.params as IMessageFromNative;
      // if (sender == "native_browser") {
      // Temp:  If message contains an exitStatus, use in place of sender.
      if (exitStatus) {
        if (exitStatus == 200) {
          onSuccess();
        } else {
          onFailure();
        }
        onExit();
      } else {
        console.debug('Handling out-of scope message');
      }
    } catch (e) {
      console.debug(e);
    }
  };

  return intercom;
};

export const useNativeDonationManagement = ({
  onSuccess: _onSuccess,
  onFailure: _onFailure,
  onExit: _onExit,
  type,
}: IOpenDonations) => {
  /** The intercom handler for the native app */
  const intercomHandler = useRef<(event: { data: string }) => void>(() => {});

  const createShortSession = useCreateShortSession();
  const queryClient = useQueryClient();

  /** Removes the event listener from the window and the isAttached ref */
  const handleRemoveEventListener = useCallback(() => {
    if (intercomHandler.current) {
      window.removeEventListener('message', intercomHandler.current);
    }
  }, []);

  /** Attach or re-attaches a new event listener to the intercom handler */
  const handleAttachEventListener = useCallback(
    (intercomParams: NativeIntercomArgs) => {
      // Remove any existing event listeners
      handleRemoveEventListener();

      const intercom = nativeIntercom(intercomParams);
      // Assign the new intercom handler to the ref for cleanup access
      intercomHandler.current = intercom;

      // Create a new event listener for the intercom handler
      window.addEventListener('message', intercom);
    },
    [handleRemoveEventListener]
  );

  /**
   *
   * Opens the native donation native window
   * Creates a window event listener for the intercom handler
   * which will catch the exit status from the native app. This handler will be removed when the component unmounts and when the intercom handler is removed.
   *
   * Creates a short session to be used for the donation
   *
   * Opens the native donation page with the short session token
   */
  const openNativeDonation = async ({
    routeParams,
    disable,
  }: {
    routeParams?: string;
    disable?: () => void;
  }) => {
    const intercomParams = {
      onSuccess: () => {
        _onSuccess?.();
      },
      onFailure: () => {
        _onFailure?.();
      },
      onExit: () => {
        void queryClient.invalidateQueries({
          queryKey: ['ledger'],
        });
        handleRemoveEventListener();

        _onExit?.();
      },
    };
    handleAttachEventListener(intercomParams);

    const shortSession = await createShortSession({ setLoader: disable });
    if (shortSession) {
      const path = `${NativeDonations[type]}?token=${shortSession?.token}${routeParams}`;
      const route = `${specificSubDomain(SUBDOMAINS.DONATE)}${path}`;
      Native.postURL(route);
      disable && setTimeout(() => disable(), 500);
    }
  };

  useEffect(() => {
    return () => {
      handleRemoveEventListener();
    };
  }, []);

  return { openNativeDonation };
};
